import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { ModalProps } from "types/utils/modal";

type Props = ModalProps;

export const HowToPayModal = ({ isOpen, close }: Props) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl">
        <Modal.Header>
          <div className="t-flex t-flex-col t-gap-1">
            <Modal.Title>How to Pay Your Estimated Taxes</Modal.Title>
            <Modal.Subtitle>
              Follow these steps to securely submit your estimated tax payments
              to the IRS using EFTPS or IRS Direct Pay after calculating your
              liability in Inkle.
            </Modal.Subtitle>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col">
            <TimelineStep n={1} title="Calculate Liability" isLast={false}>
              <p className="t-text-body-sm t-text-text-30">
                Review your calculated estimated tax liability for the current
                quarter within the Inkle dashboard to ensure accuracy before
                initiating payment.
              </p>
            </TimelineStep>

            <TimelineStep
              n={2}
              title="Visit IRS Payment Portals"
              isLast={false}
            >
              <p className="t-text-body-sm t-text-text-30">
                Navigate to the official IRS website to process your payment.
                Choose the method that best suits your corporate structure.
              </p>
              <div className="t-grid t-grid-cols-2 t-gap-3">
                <div className="t-rounded-lg t-border t-border-neutral-10 t-p-3 t-space-y-1">
                  <p className="t-text-body-sm t-font-semibold t-text-text-100">
                    EFTPS
                  </p>
                  <p className="t-text-caption t-text-text-30">
                    Required for corporations. Requires prior enrollment.
                  </p>
                  <Button
                    customType="link"
                    size="small"
                    onClick={() =>
                      window.open(
                        "https://www.eftps.gov/eftps/",
                        "_blank",
                        "noreferrer"
                      )
                    }
                  >
                    Go to EFTPS →
                  </Button>
                </div>
                <div className="t-rounded-lg t-border t-border-neutral-10 t-p-3 t-space-y-1">
                  <p className="t-text-body-sm t-font-semibold t-text-text-100">
                    Direct Pay
                  </p>
                  <p className="t-text-caption t-text-text-30">
                    For individuals/sole props paying via bank account.
                  </p>
                  <Button
                    customType="link"
                    size="small"
                    onClick={() =>
                      window.open(
                        "https://www.irs.gov/payments/direct-pay",
                        "_blank",
                        "noreferrer"
                      )
                    }
                  >
                    Go to Direct Pay →
                  </Button>
                </div>
              </div>
            </TimelineStep>

            <TimelineStep n={3} title="Enter Payment Details" isLast={false}>
              <p className="t-text-body-sm t-text-text-30">
                Carefully input the details from your Inkle calculation into the
                IRS portal. Ensure you select the correct tax year, form type
                (e.g., 1120 or 1040-ES), and payment amount.
              </p>
              <div className="t-rounded-lg t-bg-surface-lighter-grey t-p-3 t-space-y-2">
                <p className="t-text-body-sm t-font-semibold t-text-text-60">
                  Crucial Verification Points
                </p>
                <ul className="t-space-y-1">
                  {[
                    "Double-check your Employer Identification Number (EIN).",
                    'Select "Estimated Tax" as the reason for payment.',
                    "Verify the tax period (e.g., Q2 2024).",
                  ].map((point) => (
                    <li
                      key={point}
                      className="t-text-body-sm t-text-text-30 t-flex t-gap-2"
                    >
                      <span className="t-text-purple">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </TimelineStep>

            <TimelineStep n={4} title="Save Confirmation" isLast={false}>
              <p className="t-text-body-sm t-text-text-30">
                Upon successful payment, the IRS portal will generate a
                confirmation number. Save this receipt as a PDF for your records
                and upload it to the Inkle Documents portal.
              </p>
            </TimelineStep>

            <TimelineStep n={5} title="Log IRS Payment" isLast={true}>
              <p className="t-text-body-sm t-text-text-30">
                Return to Inkle and mark your estimated tax payment as complete.
                Enter the confirmation number and payment date to keep your tax
                records up to date.
              </p>
            </TimelineStep>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button>Close</Button>
          </Modal.RawClose>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const TimelineStep = ({
  n,
  title,
  isLast,
  children,
}: {
  n: number;
  title: string;
  isLast: boolean;
  children: React.ReactNode;
}) => (
  <div className="t-flex t-gap-4">
    <div className="t-flex t-flex-col t-items-center">
      <div className="t-w-8 t-h-8 t-rounded-full t-bg-purple-10 t-flex t-items-center t-justify-center t-shrink-0">
        <span className="t-text-body-sm t-font-semibold t-text-purple">
          {n}
        </span>
      </div>
      {!isLast && <div className="t-w-px t-flex-1 t-bg-neutral-10 t-my-2" />}
    </div>
    <div className="t-pb-6 t-space-y-2 t-flex-1">
      <h3 className="t-text-subtext-sm t-font-semibold t-text-text-100 t-mt-1">
        {title}
      </h3>
      {children}
    </div>
  </div>
);
